import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { EyeOff, Check, MessageCircle, Flag } from "lucide-react";

const REPORT_LABELS = {
  solicitation: "Solicitation / Promotion",
  medical:       "Medical or Unsafe Advice",
  harassment:    "Harassment",
  spam:          "Spam",
  inappropriate: "Inappropriate Content",
  other:         "Other",
};

/**
 * SharedExperienceModerationPanel — admin review for reported Shared
 * Experiences. Lives inside the existing Moderation page as a dedicated
 * tab so moderators review photo authenticity and Collective reports from
 * the same dashboard.
 *
 * Each card shows:
 *   • the originating Ask the Collective question (for context)
 *   • the Shared Experience text + author
 *   • every pending report with its category + note + reporter name
 * Actions are HIDE (soft-delete the experience + resolve reports) or
 * KEEP (clear the reported flag + resolve reports). No competitive
 * metrics, no public ranking.
 */
export default function SharedExperienceModerationPanel() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [], counts: { pending: 0, resolved: 0 } });
  const [loading, setLoading] = useState(true);

  const load = async (next = tab) => {
    setLoading(true);
    try {
      const { data: d } = await endpoints.moderationSharedExperiences(next);
      setData(d);
    } catch {
      toast.error("Couldn't load the Shared Experience reports.");
    }
    setLoading(false);
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const tabs = useMemo(() => [
    { key: "pending",  label: "Pending",  count: data.counts.pending },
    { key: "resolved", label: "Resolved", count: data.counts.resolved },
    { key: "all",      label: "All",      count: (data.counts.pending || 0) + (data.counts.resolved || 0) },
  ], [data.counts]);

  const act = async (seId, action) => {
    try {
      await endpoints.moderationSharedExperienceResolve(seId, action);
      toast.success(action === "hide" ? "Hidden from the Collective." : "Kept visible.");
      await load(tab);
    } catch {
      toast.error("Couldn't save the decision.");
    }
  };

  return (
    <section
      className="pt-10 border-t hairline reveal"
      data-testid="moderation-shared-experiences"
    >
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            The Collective
          </div>
          <h2 className="font-display text-3xl tracking-tight mt-2">
            Shared Experience reports
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl leading-relaxed">
            Members flagged these Shared Experiences. Hide the ones that cross a
            line; keep the ones that don&apos;t. Reporters stay anonymous to the
            Collective — their names are visible only here.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="moderation-se-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-pressed={tab === t.key}
              data-testid={`moderation-se-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border hairline tab-link inline-flex items-center gap-2 ${
                tab === t.key
                  ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent"
                  : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
              }`}
            >
              {t.label}
              <span className="opacity-70 tabular-nums normal-case">{t.count}</span>
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="mt-8 text-sm text-[hsl(var(--muted-foreground))]" data-testid="moderation-se-loading">
          Loading reports…
        </div>
      ) : data.items.length === 0 ? (
        <div
          className="mt-8 p-10 text-center rounded-md bg-[hsl(var(--paper))] border hairline"
          data-testid="moderation-se-empty"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            Nothing to review
          </div>
          <p className="mt-2 font-display text-xl italic">
            The Collective is quiet. Reported Shared Experiences will land here.
          </p>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {data.items.map((it) => (
            <ReportCard
              key={it.shared_experience.id}
              item={it}
              onHide={() => act(it.shared_experience.id, "hide")}
              onKeep={() => act(it.shared_experience.id, "keep")}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ReportCard({ item, onHide, onKeep }) {
  const se = item.shared_experience;
  const q = item.question || {};
  const isResolved = (item.reports || []).every((r) => r.status === "resolved");
  const isHidden = se.status === "hidden";
  return (
    <article
      className="border hairline rounded-md bg-[hsl(var(--paper))] overflow-hidden"
      data-testid={`moderation-se-card-${se.id}`}
    >
      {/* Context: the question the Shared Experience is responding to. */}
      <div className="p-5 md:p-6 border-b hairline bg-[hsl(var(--sand))]/40">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--muted-foreground))]">
          <MessageCircle size={11} /> Ask the Collective
          {q.author && (
            <>
              <span className="opacity-60">·</span>
              <span>{q.author}</span>
            </>
          )}
        </div>
        <p className="mt-2 font-display italic text-lg md:text-xl leading-snug text-[hsl(var(--fg))]/85">
          {q.content || "(question removed)"}
        </p>
      </div>

      {/* The Shared Experience itself. */}
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">
          <span>{se.author || "Member"}</span>
          {se.is_professional && (
            <span className="px-2 py-0.5 rounded-full border hairline text-[9px] tracking-[0.2em]">
              Professional
            </span>
          )}
          {isHidden && (
            <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))] text-[9px] tracking-[0.2em]">
              Hidden
            </span>
          )}
        </div>
        <p
          className="mt-2 font-display italic text-[15px] md:text-[16px] leading-relaxed text-[hsl(var(--fg))]/92"
          data-testid={`moderation-se-content-${se.id}`}
        >
          {se.content}
        </p>

        {/* Reports */}
        <div className="mt-5 pt-4 border-t hairline">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-1.5">
            <Flag size={11} />
            {(item.reports || []).length} report{item.reports?.length === 1 ? "" : "s"}
          </div>
          <ul className="mt-3 space-y-2">
            {(item.reports || []).map((r) => (
              <li key={r.id} className="text-sm">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--clay))]">
                    {REPORT_LABELS[r.category] || r.category}
                  </span>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    — {r.reporter_name}
                  </span>
                  {r.status === "resolved" && (
                    <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--moss))]">
                      Resolved
                    </span>
                  )}
                </div>
                {r.note && (
                  <p className="mt-1 text-[13px] italic text-[hsl(var(--fg))]/80">
                    &ldquo;{r.note}&rdquo;
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        {!isResolved && (
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onHide}
              data-testid={`moderation-se-hide-${se.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline text-sm bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))] tab-link hover:bg-[hsl(var(--terracotta))]/20"
            >
              <EyeOff size={13} /> Hide from the Collective
            </button>
            <button
              type="button"
              onClick={onKeep}
              data-testid={`moderation-se-keep-${se.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline text-sm text-[hsl(var(--moss))] tab-link hover:bg-[hsl(var(--moss))]/10"
            >
              <Check size={13} /> Keep visible
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
